import { Mail, MapPin, Phone } from 'lucide-react';

import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon,
  TelegramIcon,
  TiktokIcon,
  TwitterIcon,
  WhatsappIcon,
} from '@/icons';
import type { SocialLinks } from '@/state/tenant';
import type { TenantContactInfo, TenantSocialLink } from '@/helpers/tenant';
import { mergeClassNames } from '@/lib/utils';

const SOCIAL_MEDIA: Partial<
  Record<SocialLinks['type'], { icon: React.ReactNode; label: string }>
> = {
  instagram: { icon: <InstagramIcon />, label: 'Instagram' },
  twitter: { icon: <TwitterIcon />, label: 'Twitter' },
  linkedin: { icon: <LinkedinIcon />, label: 'LinkedIn' },
  facebook: { icon: <FacebookIcon />, label: 'Facebook' },
  telegram: { icon: <TelegramIcon />, label: 'Telegram' },
  whatsapp: { icon: <WhatsappIcon />, label: 'WhatsApp' },
  tiktok: { icon: <TiktokIcon />, label: 'TikTok' },
};

const InfoChip = ({
  icon,
  label,
  href,
  external = false,
}: {
  icon: React.ReactNode;
  label: string;
  href: string;
  external?: boolean;
}) => (
  <a
    href={href}
    target={external ? '_blank' : undefined}
    rel={external ? 'noopener noreferrer' : undefined}
    className="mb-focus-visible flex items-center gap-2 rounded-full bg-white px-3 py-1.5 text-sm leading-5 font-medium text-gray-700 shadow-skeu-secondary transition-[background-color,color,box-shadow,transform,opacity] duration-200 ease-out hover:bg-gray-200 active:scale-[0.97] active:duration-75 [&_svg]:h-4 [&_svg]:w-4"
  >
    <span className="flex items-center text-red-400">{icon}</span>
    {label}
  </a>
);

const SocialChip = ({ link }: { link: TenantSocialLink }) => {
  const media = SOCIAL_MEDIA[link.type];
  if (!media) return null;

  return (
    <InfoChip icon={media.icon} label={media.label} href={link.url} external />
  );
};

export const TenantContactChips = ({
  contact,
  className,
}: {
  contact: TenantContactInfo;
  className?: string;
}) => (
  <div
    className={mergeClassNames(
      'flex flex-wrap items-center gap-2.5 sm:gap-3',
      className
    )}
  >
    {contact.socialLinks.map((link) => (
      <SocialChip key={link.type} link={link} />
    ))}
    {contact.email && (
      <InfoChip
        icon={<Mail size={16} />}
        label={contact.email}
        href={`mailto:${contact.email}`}
      />
    )}
    {contact.phone && (
      <InfoChip
        icon={<Phone size={16} />}
        label={contact.phone.label}
        href={contact.phone.href}
      />
    )}
    {contact.location && (
      <InfoChip
        icon={<MapPin size={16} />}
        label={contact.location.label}
        href={contact.location.href}
        external
      />
    )}
  </div>
);
